// Die Suche des Hauptscreens. Zwei Betriebsarten, umgeschaltet ueber
// "Globale Suche" in den Einstellungen:
//
// AN (Vorgabe): global ueber alle sichtbaren Laender gleichzeitig. Belegt
// durch die Referenz-Screenshots (01.09.2026, IMG_4912 bis 4923): beim
// Durchblaettern von "BE" wechseln Flagge UND Fortschritts-Ring mit jedem
// Treffer quer durch Deutschland, Schweiz, Griechenland, Italien,
// Nordmazedonien und Norwegen.
//
// AUS: nur das in der Seitenleiste gewaehlte Land, siehe `nurLand` unten.
// Wer dort Irland antippt, will Irland sehen und nicht bei jedem Code
// zuerst Deutschland (Nutzerwunsch 10.09.2026, E77) - deshalb schaltet
// die Landwahl im Drawer diesen Schalter selbst aus, und der Eintrag
// "Laenderkennzeichen" darueber wieder an.

import type { Kennzeichen, Land } from "./bundle.js";
import type { Eintragskarte } from "./entries.js";
import type { LandSichtbarkeit } from "./einstellungen.js";

export interface Treffer {
  land: Land;
  kennzeichen: Kennzeichen;
}

/**
 * Kein Land, sondern die eigene Kategorie "Laenderkennzeichen" (scope
 * 'country') aus Block 2 des Drawers. Kein ISO-2-Code kann damit
 * kollidieren, die sind immer genau zwei Buchstaben.
 */
export const LAENDERKENNZEICHEN_MODUS = "__laenderkennzeichen__";

export interface Fortschritt {
  gesehen: number;
  gesamt: number;
  label: string;
}

/**
 * Stand des Fortschritts-Rings fuer ein Land oder fuer die
 * Laenderkennzeichen-Kategorie. Wird sowohl fuer den Leerzustand (aktives
 * Land aus den Einstellungen) als auch live beim Durchblaettern benutzt
 * (dann fuer das Land des gerade angezeigten Treffers).
 */
export function fortschrittFuer(
  landOderModus: string,
  kennzeichen: Kennzeichen[],
  laender: Land[],
  eintraege: Eintragskarte,
): Fortschritt {
  // Laenderkennzeichen (NL, PL, CZ ...) haengen zwar an einem Land in den
  // Stammdaten, zaehlen aber laut docs/decisions.md E9 NICHT in dessen
  // eigene Summe hinein, sondern nur in ihre eigene Kategorie.
  const passende =
    landOderModus === LAENDERKENNZEICHEN_MODUS
      ? kennzeichen.filter((k) => k.scope === "country")
      : kennzeichen.filter(
          (k) => k.scope !== "country" && laender.find((l) => l.iso2 === landOderModus)?.id === k.countryId,
        );

  return {
    gesehen: passende.filter((k) => eintraege.has(k.id)).length,
    gesamt: passende.length,
    label:
      landOderModus === LAENDERKENNZEICHEN_MODUS
        ? "Länderkennzeichen"
        : (laender.find((l) => l.iso2 === landOderModus)?.nameDe ?? ""),
  };
}

/**
 * Reihenfolge der Laender fuer die Suche: erst die vom Nutzer sortierten,
 * danach alle uebrigen (neu geseedete Laender zum Beispiel) nach ihrer
 * Standard-Reihenfolge aus den Stammdaten.
 */
export function vollstaendigeReihenfolge(laender: Land[], nutzerReihenfolge: string[]): string[] {
  const bekannt = new Set(nutzerReihenfolge);
  const rest = [...laender]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((l) => l.iso2)
    .filter((iso2) => !bekannt.has(iso2));
  return [...nutzerReihenfolge, ...rest];
}

/** Ohne Eintrag in countryVisibility gilt ein Land als sichtbar. */
function inGlobalerSucheSichtbar(iso2: string, sichtbarkeit: Record<string, LandSichtbarkeit>): boolean {
  return sichtbarkeit[iso2]?.search ?? true;
}

/**
 * Faellt ein Kennzeichen in den Bereich, auf den die Suche beschraenkt
 * ist?
 *
 * Zwei Fallen stecken hier drin. Erstens ist "Laenderkennzeichen" kein
 * Land: ein ISO-2-Vergleich gegen LAENDERKENNZEICHEN_MODUS geht immer
 * schief, die Suche liefe stumm leer. Ueber den Drawer ist dieser Fall
 * heute nicht erreichbar (dort schaltet die Kategorie die globale Suche
 * an), wohl aber ueber den Schalter in den Einstellungen - und wer den
 * Drawer spaeter umbaut, soll nicht in dieselbe Grube fallen. Zweitens
 * zaehlen Laenderkennzeichen
 * laut E9 nicht in ihr Gastland hinein, sondern nur in ihre eigene
 * Kategorie - dieselbe Trennung wie in fortschrittFuer(). Wer Irland
 * waehlt, bekommt also die irischen Regionalcodes und nicht "IRL".
 */
function imBereich(k: Kennzeichen, land: Land, nurLand: string): boolean {
  if (nurLand === LAENDERKENNZEICHEN_MODUS) return k.scope === "country";
  return k.scope !== "country" && land.iso2 === nurLand;
}

/**
 * Alle Treffer fuer die Eingabe, sortiert nach Laender-Reihenfolge und
 * innerhalb eines Landes nach sortKey (Laenge, dann alphabetisch).
 *
 * EXAKTES Gleichheitsmatch, kein Praefix (Referenz-Screenshots 01.09.2026:
 * Eingabe "B" zeigt in der Original-App genau Berlin fuer Deutschland,
 * nicht zusaetzlich jeden anderen mit B beginnenden Code). Wer ein
 * Kennzeichen abliest, tippt dessen Buchstaben vollstaendig ab, es wird
 * nicht nach Vorschlaegen gesucht. Pro Land kommt dabei hoechstens ein
 * Treffer heraus, weil (country_id, code) in plate_codes eindeutig ist -
 * zwei Treffer im selben Land waeren nur moeglich, wenn ein Code dort
 * zweimal mit unterschiedlicher Bedeutung vorkaeme (in der Original-App
 * bei Oesterreich beobachtet, "B" = historisch Burgenland UND aktuell
 * Bregenz), was das heutige Schema noch nicht abbildet.
 */
/** Einstellungen-Bildschirm, Phase 11: filtert vor dem Zusammenstellen der Treffer. */
export interface TrefferFilter {
  /** Aus Einstellungen "Historische Kennzeichen ausblenden". */
  historischeAusblenden?: boolean;
  /** Aus Einstellungen "Diplomatenkennzeichen anzeigen" - false blendet sie aus. */
  diplomatenAnzeigen?: boolean;
  /**
   * Aus Einstellungen "Globale Suche" aus: nur dieser eine Bereich statt
   * aller sichtbaren Laender. Entweder ein ISO-2-Code oder
   * LAENDERKENNZEICHEN_MODUS, denn beides steht im Drawer nebeneinander
   * zur Wahl und landet in derselben Einstellung `activeCountry`.
   */
  nurLand?: string;
}

export function findeTreffer(
  eingabe: string,
  kennzeichen: Kennzeichen[],
  laender: Land[],
  nutzerReihenfolge: string[],
  sichtbarkeit: Record<string, LandSichtbarkeit>,
  filter: TrefferFilter = {},
): Treffer[] {
  const gesucht = eingabe.trim().toUpperCase();
  if (gesucht.length === 0) return [];

  const landById = new Map(laender.map((l) => [l.id, l]));
  const reihenfolge = vollstaendigeReihenfolge(laender, nutzerReihenfolge);
  const rangVon = new Map(reihenfolge.map((iso2, i) => [iso2, i]));

  const treffer: Treffer[] = [];
  for (const k of kennzeichen) {
    if (k.code.toUpperCase() !== gesucht) continue;
    if (filter.historischeAusblenden && k.status === "historisch") continue;
    if (filter.diplomatenAnzeigen === false && k.scope === "diplomatic") continue;
    const land = landById.get(k.countryId);
    if (!land) continue;
    // Leerer String heisst "noch kein Land bekannt" (Bundle laedt) und
    // darf nicht als Beschraenkung auf ein Land namens "" gelten.
    if (filter.nurLand && !imBereich(k, land, filter.nurLand)) continue;
    if (!inGlobalerSucheSichtbar(land.iso2, sichtbarkeit)) continue;
    treffer.push({ land, kennzeichen: k });
  }

  treffer.sort((a, b) => {
    const rangA = rangVon.get(a.land.iso2) ?? Number.MAX_SAFE_INTEGER;
    const rangB = rangVon.get(b.land.iso2) ?? Number.MAX_SAFE_INTEGER;
    if (rangA !== rangB) return rangA - rangB;
    return a.kennzeichen.sortKey.localeCompare(b.kennzeichen.sortKey);
  });
  return treffer;
}
